import { tenantTodayDate } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { fmtCurrency } from "../../lib/display-labels";
import { fmtAmount } from "../../lib/currency-amounts";

interface DayData {
  day: string;
  revenue: number;
}

interface RevenuePulseProps {
  data: DayData[];
  /** The period's total, or null when there is no chart to total (no tenant zone). */
  total: number | null;
  period: 'week' | 'month';
  /**
   * The total's currency, when the read carries one (the global route, api#1121).
   * Absent, the total reads "$" exactly as the casing route always has.
   */
  currency?: string;
}

/**
 * The bars and the total. The failure and loading states are the widget's
 * (RevenuePulseWidget): this component is only ever handed a read that arrived,
 * so its $0.00 is a real $0.00.
 */
const RevenuePulse = ({ data, total, period, currency }: RevenuePulseProps) => {
  const timeZone = useTenantTimeZone();
  const maxVal = Math.max(...data.map((d) => d.revenue), 1);
  // The tenant's weekday, not the device's. No zone, no highlighted bar.
  const tenantToday = tenantTodayDate(timeZone);
  const todayIdx = period === 'week' && tenantToday ? (tenantToday.getDay() + 6) % 7 : -1; // 0=Mon

  return (
    <div>
      <div className="mb-4 flex items-baseline gap-2">
        <span data-testid="revenue-total" className="text-2xl font-extrabold tabular-nums text-fieldiq-text">
          {currency && total !== null ? fmtAmount({ currency, amount: total }) : fmtCurrency(total)}
        </span>
        <span className="text-xs text-fieldiq-muted">this {period}</span>
      </div>

      <div data-testid="revenue-chart" className="flex h-28 items-end gap-2">
        {data.map((d, i) => {
          const barH = maxVal > 0 ? (d.revenue / maxVal) * 100 : 0;
          const isToday = i === todayIdx;
          return (
            <div key={d.day} className="flex flex-1 flex-col items-center gap-1">
              <div data-testid="revenue-track" className="relative flex h-24 w-full items-end justify-center">
                <div
                  data-testid="revenue-bar"
                  className={`w-full max-w-[28px] rounded-t-fi-control ${
                    isToday ? "bg-fi-signal-amber" : d.revenue > 0 ? "bg-fi-signal-blue" : "bg-fieldiq-dark"
                  }`}
                  style={{ height: `${barH}%`, minHeight: d.revenue > 0 ? 4 : 0 }}
                />
              </div>
              <span className={`text-[10px] ${isToday ? "font-black text-fieldiq-text" : "font-semibold text-fieldiq-muted"}`}>
                {d.day}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RevenuePulse;
